/**
 * Home Dashboard
 * SCR-002: Consumer home dashboard with quick actions and document overview
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-002 Home Dashboard
 * @iso27001 A.9.4.1 Information access restriction
 * @iso27001 A.12.4.1 Event logging
 *
 * UX Intent (from SCR-002):
 * - confirmation: none (dashboard is read-only view)
 * - error_handling: toast (non-blocking errors)
 * - success_feedback: toast (for actions like dismiss tip)
 * - friction_intent: none (low-friction browsing)
 * - emotional_state: neutral (informational)
 * - risk_assessment: low (standard screen with reversible actions)
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useToast } from '@myvault/shared/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Icon,
  Badge,
  ProgressBar,
} from '@myvault/shared/components';
import { NalatenschapDossierCard } from '../nalatenschap';

// ============= Type Extensions =============

// Extend Window for PostHog analytics
declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

// ============= Types =============

interface DashboardStats {
  documentsTotal: number;
  documentsThisMonth: number;
  storageUsedBytes: number;
  storageLimitBytes: number;
  delegatesActive: number;
  delegatesPending: number;
}

interface RecentDocument {
  id: string;
  title: string;
  category: string;
  uploadedAt: string;
  thumbnailUrl?: string;
  mimeType: string;
}

interface ActivityItem {
  id: string;
  action: string;
  description: string;
  timestamp: string;
  icon: 'document' | 'share' | 'user' | 'shield' | 'ai';
}

interface UpcomingReminder {
  id: string;
  title: string;
  dueDate: string;
  documentId: string;
  type: 'expiry' | 'renewal' | 'review';
}

interface DashboardData {
  stats: DashboardStats;
  recentDocuments: RecentDocument[];
  activity: ActivityItem[];
  upcomingReminders: UpcomingReminder[];
  tips: string[];
}

// ============= API =============

const API_BASE = '/api/v1';

interface ApiDashboardResponse {
  stats: {
    total_documents: number;
    documents_this_month: number;
    storage_used: number;
    storage_limit: number;
    ai_credits_remaining: number;
    ai_credits_monthly: number;
    active_delegates: number;
    pending_invitations: number;
  };
  // API returns camelCase keys
  recentDocuments: Array<{
    id: string;
    name: string;
    category: string;
    category_color: string;
    type_icon: string;
    uploaded_ago: string;
    has_ai_summary: boolean;
    is_shared: boolean;
  }>;
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    target_name: string;
    target_href: string;
    time_ago: string;
    icon: string;
    color: string;
    actor: { name: string; initials: string };
  }>;
  reminders: Array<{
    id: string;
    title: string;
    due_date: string;
    document_id: string;
    type: string;
  }>;
  pendingActions: unknown[];
}

async function fetchDashboardData(): Promise<DashboardData> {
  const token = localStorage.getItem('myvault_access_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/dashboard`, {
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    throw new Error('Failed to fetch dashboard data');
  }

  const apiData: ApiDashboardResponse = await response.json();

  // Transform API response to frontend format
  return {
    stats: {
      documentsTotal: apiData.stats.total_documents,
      documentsThisMonth: apiData.stats.documents_this_month,
      storageUsedBytes: apiData.stats.storage_used,
      storageLimitBytes: apiData.stats.storage_limit,
      delegatesActive: apiData.stats.active_delegates,
      delegatesPending: apiData.stats.pending_invitations,
    },
    recentDocuments: (apiData.recentDocuments || []).map(doc => ({
      id: doc.id,
      title: doc.name,
      category: doc.category,
      uploadedAt: doc.uploaded_ago,
      mimeType: doc.type_icon === 'photo' ? 'image/jpeg' : 'application/pdf',
    })),
    activity: (apiData.recentActivity || []).map(act => ({
      id: act.id,
      action: act.type,
      description: act.description,
      timestamp: act.time_ago,
      icon: (act.type === 'document_shared' ? 'share' :
             act.type === 'delegate_invited' ? 'user' :
             act.type === 'security_alert' ? 'shield' : 'document') as ActivityItem['icon'],
    })),
    upcomingReminders: (apiData.reminders || []).map(rem => ({
      id: rem.id,
      title: rem.title,
      dueDate: rem.due_date,
      documentId: rem.document_id,
      type: rem.type as UpcomingReminder['type'],
    })),
    tips: [
      'Upload regelmatig je belangrijke documenten om ze veilig te bewaren.',
      'Gebruik de AI-samenvatting om complexe documenten snel te begrijpen.',
      'Nodig een familielid uit als gemachtigde voor noodgevallen.',
    ],
  };
}

// ============= Helper Functions =============

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function getDocumentIcon(mimeType: string): 'document' | 'photo' | 'document' {
  if (mimeType.startsWith('image/')) return 'photo';
  return 'document';
}

function getReminderVariant(type: string): 'warning' | 'info' | 'default' {
  switch (type) {
    case 'expiry':
      return 'warning';
    case 'renewal':
      return 'info';
    default:
      return 'default';
  }
}

// ============= Components =============

function WelcomeCard({ name }: { name: string }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Goedemorgen' : hour < 18 ? 'Goedemiddag' : 'Goedenavond';

  return (
    <div className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
      <h1 className="text-2xl font-bold">
        {greeting}, {name}!
      </h1>
      <p className="mt-2 text-blue-100">
        Welkom terug bij je persoonlijke documentenkluis.
      </p>
    </div>
  );
}

function QuickActionsGrid() {
  const actions = [
    {
      label: 'Upload document',
      description: 'Voeg een nieuw document toe',
      icon: 'arrowUpTray' as const,
      href: '/documents/upload',
      color: 'bg-blue-600',
    },
    {
      label: 'Bekijk documenten',
      description: 'Alle opgeslagen bestanden',
      icon: 'folder' as const,
      href: '/documents',
      color: 'bg-amber-500',
    },
    {
      label: 'Privacybescherming',
      description: 'Bescherm gevoelige gegevens',
      icon: 'shieldCheck' as const,
      href: '/settings/redaction/templates',
      color: 'bg-green-600',
    },
    {
      label: 'Deel met iemand',
      description: 'Nodig een gemachtigde uit',
      icon: 'userPlus' as const,
      href: '/delegates/manage',
      color: 'bg-green-600',
    },
    {
      label: 'Marketplace',
      description: 'Vind een adviseur of accountant',
      icon: 'buildingStorefront' as const,
      href: '/marketplace',
      color: 'bg-indigo-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {actions.map((action) => (
        <Link
          key={action.label}
          to={action.href}
          className="group rounded-lg border border-gray-200 bg-white p-4 transition-all hover:border-blue-300 hover:shadow-md"
        >
          <div
            className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg ${action.color}`}
          >
            <Icon name={action.icon} variant="solid" size="sm" className="text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
            {action.label}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{action.description}</p>
        </Link>
      ))}
    </div>
  );
}

function RecentDocumentsList({ documents }: { documents: RecentDocument[] }) {
  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Icon name="documentPlus" variant="outline" size="lg" className="mb-3 text-gray-400" />
        <p className="text-gray-600">Nog geen documenten geupload</p>
        <Link
          to="/documents/upload"
          className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Upload je eerste document
        </Link>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {documents.map((doc) => (
        <li key={doc.id}>
          <Link
            to={`/documents/${doc.id}`}
            className="flex items-center gap-4 py-3 hover:bg-gray-50"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
              <Icon
                name={getDocumentIcon(doc.mimeType)}
                variant="outline"
                size="sm"
                className="text-gray-600"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-gray-900">{doc.title}</p>
              <p className="text-sm text-gray-500">
                {doc.category} • {doc.uploadedAt}
              </p>
            </div>
            <Icon name="chevronRight" variant="outline" size="sm" className="text-gray-400" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function DocumentStatsCard({ stats }: { stats: DashboardStats }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-lg bg-blue-50 p-4">
        <div className="flex items-center gap-2">
          <Icon name="document" variant="solid" size="sm" className="text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Documenten</span>
        </div>
        <p className="mt-2 text-2xl font-bold text-blue-900">{stats.documentsTotal}</p>
        {stats.documentsThisMonth > 0 && (
          <p className="mt-1 text-sm text-blue-600">
            +{stats.documentsThisMonth} deze maand
          </p>
        )}
      </div>
      <div className="rounded-lg bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <Icon name="userGroup" variant="solid" size="sm" className="text-green-600" />
          <span className="text-sm font-medium text-green-900">Gemachtigden</span>
        </div>
        <p className="mt-2 text-2xl font-bold text-green-900">{stats.delegatesActive}</p>
        {stats.delegatesPending > 0 && (
          <p className="mt-1 text-sm text-amber-600">
            {stats.delegatesPending} in afwachting
          </p>
        )}
      </div>
    </div>
  );
}

function StorageUsageCard({ stats }: { stats: DashboardStats }) {
  const usagePercent = Math.round((stats.storageUsedBytes / stats.storageLimitBytes) * 100);
  const isNearLimit = usagePercent >= 80;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Opslaggebruik</span>
        <span className="text-sm text-gray-500">
          {formatBytes(stats.storageUsedBytes)} / {formatBytes(stats.storageLimitBytes)}
        </span>
      </div>
      <ProgressBar
        value={usagePercent}
        max={100}
        variant={isNearLimit ? 'warning' : 'default'}
        size="md"
      />
      {isNearLimit && (
        <p className="mt-2 text-sm text-amber-600">
          Je opslag raakt vol.{' '}
          <Link to="/subscription/change" className="font-medium underline">
            Upgrade je plan
          </Link>
        </p>
      )}
    </div>
  );
}

function ActivityFeed({ activity }: { activity: ActivityItem[] }) {
  const iconMap = {
    document: 'document' as const,
    share: 'share' as const,
    user: 'user' as const,
    shield: 'shield' as const,
    ai: 'sparkles' as const,
  };

  if (activity.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-500">Nog geen recente activiteit</p>
    );
  }

  return (
    <ul className="space-y-3">
      {activity.slice(0, 5).map((item) => (
        <li key={item.id} className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
            <Icon
              name={iconMap[item.icon] || 'informationCircle'}
              variant="outline"
              size="sm"
              className="text-gray-600"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-900">{item.description}</p>
            <p className="text-xs text-gray-500">{item.timestamp}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function UpcomingRemindersCard({ reminders }: { reminders: UpcomingReminder[] }) {
  if (reminders.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-500">Geen aankomende herinneringen</p>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {reminders.slice(0, 3).map((reminder) => (
        <li key={reminder.id} className="py-3">
          <Link
            to={`/documents/${reminder.documentId}`}
            className="flex items-center justify-between hover:bg-gray-50"
          >
            <div>
              <p className="font-medium text-gray-900">{reminder.title}</p>
              <p className="text-sm text-gray-500">
                {new Date(reminder.dueDate).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                })}
              </p>
            </div>
            <Badge variant={getReminderVariant(reminder.type)}>
              {reminder.type === 'expiry'
                ? 'Verloopt'
                : reminder.type === 'renewal'
                ? 'Verlengen'
                : 'Review'}
            </Badge>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function TipsCard({ tips }: { tips: string[] }) {
  if (tips.length === 0) return null;

  const tip = tips[Math.floor(Math.random() * tips.length)];

  return (
    <div className="rounded-lg border-l-4 border-l-amber-500 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <Icon name="lightBulb" variant="solid" size="sm" className="mt-0.5 text-amber-600" />
        <div>
          <p className="font-medium text-amber-900">Tip van de dag</p>
          <p className="mt-1 text-sm text-amber-800">{tip}</p>
        </div>
      </div>
    </div>
  );
}

function UpgradePrompt({ currentPlan }: { currentPlan: string }) {
  if (currentPlan !== 'free') return null;

  return (
    <div className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Upgrade naar Premium</h3>
          <p className="mt-1 text-sm text-blue-100">
            Krijg onbeperkte opslag en AI-analyses voor al je documenten.
          </p>
        </div>
        <Link
          to="/subscription/change"
          className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50"
        >
          Bekijk plannen
        </Link>
      </div>
    </div>
  );
}

// ============= Loading State =============

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-32 animate-pulse rounded-xl bg-gray-200" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-gray-200" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="h-64 animate-pulse rounded-lg bg-gray-200 lg:col-span-2" />
        <div className="h-64 animate-pulse rounded-lg bg-gray-200" />
      </div>
    </div>
  );
}

// ============= Analytics Tracking =============
// SCR-002: analytics_events from specification

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
}

function trackEvent({ event, properties }: AnalyticsEvent) {
  // Analytics integration point - sends to PostHog/Mixpanel when configured
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(event, properties);
  }
  // Also log in development
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Analytics]', event, properties);
  }
}

// ============= Main Component =============

export function HomeDashboard() {
  const { user } = useAuth();
  const { error: showError } = useToast();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboardData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });

  // SCR-002 UX Intent: error_handling: toast (non-blocking)
  // Show toast notification for errors instead of blocking UI
  useEffect(() => {
    if (error) {
      showError('Kon dashboard niet laden', 'Probeer de pagina te vernieuwen');
    }
  }, [error, showError]);

  // SCR-002: Track dashboard_viewed analytics event
  useEffect(() => {
    if (data && user) {
      trackEvent({
        event: 'dashboard_viewed',
        properties: {
          user_role: user.role || 'consumer',
          subscription_plan: user.subscriptionPlan || 'free',
          document_count: data.stats.documentsTotal,
        },
      });
    }
  }, [data, user]);

  if (isLoading) {
    return (
      <div className="p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  // SCR-002 UX Intent: error_handling: toast
  // Even on error, show the dashboard with empty/fallback data
  // The toast notification alerts the user non-blocking

  // Default data for when API returns empty
  const dashboardData: DashboardData = data || {
    stats: {
      documentsTotal: 0,
      documentsThisMonth: 0,
      storageUsedBytes: 0,
      storageLimitBytes: 1073741824, // 1GB default
      delegatesActive: 0,
      delegatesPending: 0,
    },
    recentDocuments: [],
    activity: [],
    upcomingReminders: [],
    tips: [
      'Upload regelmatig je belangrijke documenten om ze veilig te bewaren.',
      'Gebruik de AI-samenvatting om complexe documenten snel te begrijpen.',
      'Nodig een familielid uit als gemachtigde voor noodgevallen.',
    ],
  };

  const userName = user?.firstName || user?.displayName?.split(' ')[0] || 'Gebruiker';

  return (
    <div className="p-6">
      {/* SCR-002 Accessibility: main landmark with ARIA label */}
      <main
        className="mx-auto max-w-7xl space-y-6"
        aria-label="Dashboard koptekst met welkomstbericht en upload knop"
      >
        {/* Welcome Card */}
        <WelcomeCard name={userName} />

        {/* Quick Actions - SCR-002 ARIA: quick-actions */}
        <section aria-label="Snelle acties menu">
          <QuickActionsGrid />
        </section>

        {/* Nalatenschap Dossier Card */}
        <NalatenschapDossierCard />

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Recent Documents */}
          <div className="space-y-6 lg:col-span-2">
            {/* SCR-002 ARIA: recent-documents */}
            <section aria-label="Lijst met recent geopende documenten">
              <Card>
                <CardHeader className="flex items-center justify-between">
                  <CardTitle>Recente documenten</CardTitle>
                  <Link
                    to="/documents"
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Bekijk alles
                  </Link>
                </CardHeader>
                <CardContent>
                  <RecentDocumentsList documents={dashboardData.recentDocuments} />
                </CardContent>
              </Card>
            </section>

            {/* Stats Row - SCR-002 ARIA: quick-stats */}
            <section aria-label="Snelle statistieken overzicht">
              <Card>
                <CardContent className="py-4">
                  <DocumentStatsCard stats={dashboardData.stats} />
                </CardContent>
              </Card>
            </section>

            {/* Storage Usage */}
            <Card>
              <CardContent className="py-4">
                <StorageUsageCard stats={dashboardData.stats} />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Activity & Reminders */}
          <div className="space-y-6">
            {/* SCR-002 ARIA: activity-feed */}
            <section aria-label="Recente activiteit tijdlijn">
              <Card>
                <CardHeader>
                  <CardTitle>Recente activiteit</CardTitle>
                </CardHeader>
                <CardContent>
                  <ActivityFeed activity={dashboardData.activity} />
                </CardContent>
              </Card>
            </section>

            {/* SCR-002 ARIA: upcoming-reminders */}
            <section aria-label="Aankomende herinneringen">
              <Card>
                <CardHeader>
                  <CardTitle>Aankomende herinneringen</CardTitle>
                </CardHeader>
                <CardContent>
                  <UpcomingRemindersCard reminders={dashboardData.upcomingReminders} />
                </CardContent>
              </Card>
            </section>

            {/* Tips */}
            <TipsCard tips={dashboardData.tips} />
          </div>
        </div>

        {/* Upgrade Prompt (for free users) */}
        <UpgradePrompt currentPlan={user?.subscriptionPlan || 'free'} />
      </main>
    </div>
  );
}

export default HomeDashboard;
